import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, Show } from "solid-js";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDrizzle } from "@/hooks/use-drizzle";
import {
  createSavedViewMutation,
  deleteSavedViewMutation,
  parseSavedViewFilters,
  type SavedViewFilters,
  updateSavedViewMutation,
} from "@listto/shared";
import type { SavedView } from "@listto/powersync";
import { Button } from "@/components/ui/button";
import { Label, Switch, TextField } from "@fleet/ui";
import { useSession } from "@/lib/auth";
import { Filter, Trash2 } from "@fleet/icons";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";

interface FilterRowProps {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  testId: string;
}

function FilterRow(props: FilterRowProps) {
  return (
    <div class="flex items-center justify-between rounded-xl border border-border/20 px-4 py-3">
      <span class="text-sm font-medium">{props.label}</span>
      <span data-testid={props.testId}>
        <Switch checked={props.checked} onCheckedChange={props.onChange} aria-label={props.label} />
      </span>
    </div>
  );
}

interface SavedViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing view to edit; null/undefined = create a new one. */
  view?: SavedView | null;
  /** Notification after deletion (to exit view route if active). */
  onDeleted?: (id: string) => void;
}

/**
 * Creation/edition of a saved smart view: name + filters (shape of
 * `UseTodosOptions`). Boolean filters go as switches; text search
 * as free field. The resulting view is a named `getTodosQuery`
 * that syncs across devices (`saved_view` table).
 */
export function SavedViewDialog(props: SavedViewDialogProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const session = useSession();

  const [name, setName] = createSignal("");
  const [importantOnly, setImportantOnly] = createSignal(false);
  const [plannedOnly, setPlannedOnly] = createSignal(false);
  const [myDayOnly, setMyDayOnly] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  // Repopulates form upon opening (create = empty, edit = current filters).
  createEffect(() => {
    if (!props.open) {
      return;
    }
    const filters = parseSavedViewFilters(props.view?.filters);
    setName(props.view?.name ?? "");
    setImportantOnly(filters.importantOnly === true);
    setPlannedOnly(filters.plannedOnly === true);
    setMyDayOnly(filters.myDayOnly === true);
    setSearchQuery(filters.searchQuery ?? "");
  });

  const isEditing = () => Boolean(props.view);
  const isReady = () => Boolean(drizzle()) && Boolean(session.userId());

  const collectFilters = (): SavedViewFilters => ({
    importantOnly: importantOnly(),
    plannedOnly: plannedOnly(),
    myDayOnly: myDayOnly(),
    searchQuery: searchQuery().trim() || undefined,
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!name().trim() || !dbInstance || !uid) {
      return;
    }
    setIsSubmitting(true);
    try {
      await (props.view
        ? updateSavedViewMutation(dbInstance, props.view.id, {
            name: name().trim(),
            filters: collectFilters(),
          })
        : createSavedViewMutation(dbInstance, uid, name().trim(), collectFilters()));
      props.onOpenChange(false);
    } catch (err) {
      console.error("Error saving view:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Receives view as argument (from JSX handler) to avoid reading `props`
  // outside a tracked scope (solid/reactivity).
  const handleDelete = async (view: SavedView | null | undefined) => {
    const dbInstance = drizzle();
    if (!dbInstance || !view) {
      return;
    }
    try {
      await deleteSavedViewMutation(dbInstance, view.id);
      props.onOpenChange(false);
      props.onDeleted?.(view.id);
    } catch (err) {
      console.error("Error deleting view:", err);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <DialogTitle class="text-xl font-bold">
          {isEditing() ? t(msg`Edit view`) : t(msg`New saved view`)}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={(e) => void handleSubmit(e)} class="space-y-5 p-6">
        <div class="flex items-center gap-4">
          <div class="h-16 w-16 rounded-3xl bg-muted flex items-center justify-center text-muted-foreground">
            <Filter class="h-8 w-8" />
          </div>
          <div class="flex-1 space-y-2">
            <Label
              for="saved-view-name"
              class="text-xs uppercase tracking-wider font-bold opacity-50"
            >
              {t(msg`View name`)}
            </Label>
            <TextField
              id="saved-view-name"
              data-testid="saved-view-name"
              value={name()}
              onInput={(e) => void setName(e.currentTarget.value)}
              placeholder={t(msg`e.g. Urgent, This week...`)}
              autofocus
              class="bg-transparent border-none p-0 text-xl font-bold focus-visible:ring-0"
            />
          </div>
        </div>

        <div class="space-y-2">
          <span class="block text-xs uppercase tracking-wider font-bold opacity-50">
            {t(msg`Filters`)}
          </span>
          <FilterRow
            label={t(msg`Only important tasks`)}
            checked={importantOnly()}
            onChange={setImportantOnly}
            testId="saved-view-filter-important"
          />
          <FilterRow
            label={t(msg`Only tasks with due date`)}
            checked={plannedOnly()}
            onChange={setPlannedOnly}
            testId="saved-view-filter-planned"
          />
          <FilterRow
            label={t(msg`Only My Day tasks`)}
            checked={myDayOnly()}
            onChange={setMyDayOnly}
            testId="saved-view-filter-myday"
          />
          <div class="space-y-1.5 pt-1">
            <Label
              for="saved-view-search"
              class="text-xs uppercase tracking-wider font-bold opacity-50"
            >
              {t(msg`Text contains`)}
            </Label>
            <TextField
              id="saved-view-search"
              data-testid="saved-view-search"
              value={searchQuery()}
              onInput={(e) => void setSearchQuery(e.currentTarget.value)}
              placeholder={t(msg`Optional keyword`)}
            />
          </div>
        </div>

        <div class="flex items-center justify-between pt-4 border-t border-border/10">
          <Show when={isEditing()}>
            <ConfirmDialog
              title={t(msg`Delete view?`)}
              description={t(msg`The saved view will be removed. Your tasks are not affected.`)}
              confirmLabel={t(msg`Delete`)}
              variant="destructive"
              onConfirm={() => void handleDelete(props.view)}
              renderTrigger={(open) => (
                <Button
                  type="button"
                  variant="ghost"
                  class="text-destructive hover:bg-destructive/10 h-11 rounded-xl px-4"
                  disabled={!isReady()}
                  onClick={open}
                  data-testid="saved-view-delete"
                >
                  <Trash2 class="h-4 w-4 mr-2" />
                  {t(msg`Delete`)}
                </Button>
              )}
            />
          </Show>
          <div class="flex items-center gap-3 ml-auto">
            <Button
              type="button"
              variant="ghost"
              class="h-11 px-6 rounded-xl font-bold"
              onClick={() => {
                props.onOpenChange(false);
              }}
            >
              {t(msg`Cancel`)}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting() || !name().trim() || !isReady()}
              class="h-11 px-8 font-bold rounded-xl"
              data-testid="saved-view-submit"
            >
              {isEditing() ? t(msg`Save changes`) : t(msg`Create view`)}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
